/* eslint-disable react/no-array-index-key */

import { PropertyType } from '@/api/enumerations';
import { AggregatedFactors, FactorData } from '@/api/workOrders/types';
import { independentStructuresGroup } from '@/constants/selectOptions';
import { fmtCurrency, formatFloatNumberDot } from '@/helpers';
import {
  Box,
  Divider,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { memo } from 'react';

import { GroupStatusIcon } from '../../components/StatusIndicators';
import {
  BoldCell,
  CellCaption,
  CellContent,
  CellHeader,
  columnWidth,
  itemColumnWidth,
  RowCaption,
  RowHeader,
  StyledTable,
} from '../../styles';
import { Constants } from '../constants';
import {
  isFactorsComplete,
  isHomogenizationComplete,
} from '../utils/completionCheckers';

interface HomogenizationProps {
  factorsData: FactorData[];
  propertyType: PropertyType;
  aggregatedFactors: AggregatedFactors[];
}

interface HeaderItem {
  label: string;
}

export const Homogenization = memo(function Homogenization({
  factorsData,
  propertyType,
  aggregatedFactors,
}: HomogenizationProps): JSX.Element {
  let dynamicHeaders: HeaderItem[] = [];

  if (
    factorsData.length > 0 &&
    factorsData[0].columns &&
    factorsData[0].columns.length > 0
  ) {
    dynamicHeaders = factorsData[0].columns.map((col) => ({
      label: col.name || '',
    }));
  } else if (
    aggregatedFactors.length > 0 &&
    independentStructuresGroup.has(propertyType)
  ) {
    dynamicHeaders = aggregatedFactors.map((factor) => ({
      label: factor.name || '',
    }));
  }

  const homogenizationHeaders: HeaderItem[] = [
    { label: Constants.item },
    ...dynamicHeaders,
    { label: Constants.totalPrice },
    { label: Constants.unitValue },
    { label: Constants.offer },
    { label: Constants.offerFactor },
  ];

  const factorsHeaders: HeaderItem[] = [
    { label: Constants.item },
    ...dynamicHeaders,
    { label: Constants.homogenizedValue },
    { label: Constants.sanitizedValue },
    { label: Constants.adjustmentInterval },
  ];

  const aggFactorsHeaders = [
    { label: '', width: itemColumnWidth },
    { label: 'Nome', width: columnWidth },
    { label: 'Média', width: columnWidth },
    { label: 'Desvio Padrão', width: columnWidth },
    { label: 'Coeficiente de Variação', width: columnWidth },
    { label: 'Validação de Coeficientes', width: columnWidth },
  ];

  return (
    <Box>
      <Box id="fac-homogenization" display="flex" alignItems="center" gap={1}>
        <GroupStatusIcon isComplete={isHomogenizationComplete(factorsData)} />
        <Typography my={2} fontWeight={600}>
          Elementos e Homogeneização
        </Typography>
      </Box>
      <TableContainer sx={{ mb: 3 }}>
        <StyledTable aria-label={Constants.homogenizationTable}>
          <TableHead>
            <RowCaption>
              <CellCaption colSpan={homogenizationHeaders.length}>
                {Constants.homogenizationCaption}
              </CellCaption>
            </RowCaption>
            <RowHeader>
              {homogenizationHeaders.map((header, ind) => {
                const width =
                  header.label === 'Item' ? itemColumnWidth : columnWidth;
                return (
                  <CellHeader
                    align="center"
                    key={ind}
                    sx={{ width, minWidth: width, maxWidth: width }}
                  >
                    {header.label}
                  </CellHeader>
                );
              })}
            </RowHeader>
          </TableHead>
          <TableBody>
            {factorsData.map((factor, index) => (
              <TableRow key={index}>
                <BoldCell align="center">{index + 1}</BoldCell>
                {factor.columns?.map((column, colIndex) => (
                  <CellContent
                    align="center"
                    key={colIndex}
                    sx={{ width: columnWidth, minWidth: columnWidth }}
                  >
                    <Typography>{column.homo_value || '-'}</Typography>
                  </CellContent>
                ))}
                <CellContent align="center">
                  {factor.input_data.total_price
                    ? formatFloatNumberDot(factor.input_data.total_price)
                    : '-'}
                </CellContent>
                <CellContent align="center">
                  {factor.input_data.price_per_square_meters
                    ? formatFloatNumberDot(
                        factor.input_data.price_per_square_meters
                      )
                    : '-'}
                </CellContent>
                <CellContent align="center">
                  <Typography>{factor.input_data.offer || '-'}</Typography>
                </CellContent>
                <CellContent align="center">
                  <Typography>
                    {factor.input_data.offer_factor != null
                      ? `${factor.input_data.offer_factor}%`
                      : '-'}
                  </Typography>
                </CellContent>
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </TableContainer>
      <Divider />
      <Box id="fac-factors" display="flex" alignItems="center" gap={1}>
        <GroupStatusIcon
          isComplete={isFactorsComplete(factorsData, aggregatedFactors)}
        />
        <Typography my={3} fontWeight={600}>
          {Constants.factors}
        </Typography>
      </Box>
      <TableContainer>
        <StyledTable aria-label={Constants.factorsTable}>
          <TableHead>
            <RowCaption>
              <CellCaption colSpan={factorsHeaders.length}>
                {Constants.factorsCaption}
              </CellCaption>
            </RowCaption>
            <RowHeader>
              {factorsHeaders.map((header, idx) => {
                const width =
                  header.label === 'Item' ? itemColumnWidth : columnWidth;
                return (
                  <CellHeader
                    align="center"
                    key={idx}
                    sx={{ width, minWidth: width, maxWidth: width }}
                  >
                    {header.label || '-'}
                  </CellHeader>
                );
              })}
            </RowHeader>
          </TableHead>
          <TableBody>
            {factorsData.map((factor, index) => (
              <TableRow key={index}>
                <CellContent
                  align="center"
                  sx={{ width: itemColumnWidth, minWidth: itemColumnWidth }}
                >
                  {index + 1}
                </CellContent>
                {factor.columns?.map((column, colIndex) => (
                  <CellContent
                    align="center"
                    key={colIndex}
                    sx={{ width: columnWidth, minWidth: columnWidth }}
                  >
                    <Typography>{column.value ?? '-'}</Typography>
                  </CellContent>
                ))}
                <CellContent
                  align="center"
                  sx={{ width: columnWidth, minWidth: columnWidth }}
                >
                  <Typography fontWeight={600}>
                    {fmtCurrency(factor.homogenized_price)}
                  </Typography>
                </CellContent>
                <CellContent
                  align="center"
                  sx={{ width: columnWidth, minWidth: columnWidth }}
                >
                  <Typography fontWeight={600}>
                    {fmtCurrency(factor.sanitized_price)}
                  </Typography>
                </CellContent>
                <CellContent
                  align="center"
                  sx={{ width: columnWidth, minWidth: columnWidth }}
                >
                  <Typography>
                    {factor.admissible_adj_interval ?? '-'}
                  </Typography>
                </CellContent>
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </TableContainer>
      <TableContainer sx={{ marginTop: 4 }}>
        <StyledTable aria-label={Constants.aggregatedFactorsTable}>
          <TableHead>
            <RowHeader>
              {aggFactorsHeaders.map(({ label, width }, idx) => (
                <CellHeader
                  align="center"
                  key={idx}
                  sx={{ width, minWidth: width, maxWidth: width }}
                >
                  {label}
                </CellHeader>
              ))}
            </RowHeader>
          </TableHead>
          <TableBody>
            {aggregatedFactors.map((factor, colIndex) => (
              <TableRow key={colIndex}>
                <CellContent
                  align="center"
                  sx={{ width: itemColumnWidth, minWidth: itemColumnWidth }}
                >
                  {colIndex + 1}
                </CellContent>
                <CellContent
                  align="center"
                  sx={{ width: columnWidth, minWidth: columnWidth }}
                >
                  <Typography>{factor.name || '-'}</Typography>
                </CellContent>
                <CellContent
                  align="center"
                  sx={{ width: columnWidth, minWidth: columnWidth }}
                >
                  <Typography fontWeight={600}>
                    {fmtCurrency(factor.mean_ppsm)}
                  </Typography>
                </CellContent>
                <CellContent
                  align="center"
                  sx={{ width: columnWidth, minWidth: columnWidth }}
                >
                  <Typography fontWeight={600}>
                    {fmtCurrency(factor.std_ppsm)}
                  </Typography>
                </CellContent>
                <CellContent
                  align="center"
                  sx={{ width: columnWidth, minWidth: columnWidth }}
                >
                  <Typography>
                    {factor.coeff != null ? `${factor.coeff}%` : '-'}
                  </Typography>
                </CellContent>
                <CellContent
                  align="center"
                  sx={{ width: columnWidth, minWidth: columnWidth }}
                >
                  <Typography>
                    {factor.coeff_validation != null ? (
                      <span>{factor.coeff_validation ? 'Sim' : 'Não'}</span>
                    ) : (
                      '-'
                    )}
                  </Typography>
                </CellContent>
              </TableRow>
            ))}
          </TableBody>
        </StyledTable>
      </TableContainer>
      <Divider />
    </Box>
  );
});
